/**
 * Analytics helpers — GTM, GA4, Meta Pixel, Google Ads, OpenAI Ads Pixel.
 * Configure via REACT_APP_* env vars (see .env).
 */

import { track } from './tracker';

const parseBool = (value, defaultValue = true) => {
  if (value === undefined || value === null || value === '') return defaultValue;
  return String(value).toLowerCase() === 'true' || value === '1';
};

export const analyticsConfig = {
  enabled: parseBool(process.env.REACT_APP_ANALYTICS_ENABLED, true),
  gtmId: (process.env.REACT_APP_GTM_ID || '').trim(),
  ga4Id: (process.env.REACT_APP_GA4_MEASUREMENT_ID || '').trim(),
  /** When true + GTM is set, GA4 gtag is not loaded directly (configure GA4 inside GTM). */
  ga4ViaGtm: parseBool(process.env.REACT_APP_GA4_VIA_GTM, false),
  metaPixelId: (process.env.REACT_APP_META_PIXEL_ID || '').trim(),
  googleAdsId: (process.env.REACT_APP_GOOGLE_ADS_ID || '').trim(),
  openAiPixelId: (process.env.REACT_APP_OPENAI_ADS_PIXEL_ID || '').trim(),
  debug: parseBool(process.env.REACT_APP_ANALYTICS_DEBUG, false),
};

const log = (...args) => {
  if (analyticsConfig.debug) {
    // eslint-disable-next-line no-console
    console.log('[analytics]', ...args);
  }
};

export function isAnalyticsEnabled() {
  return analyticsConfig.enabled && typeof window !== 'undefined';
}

const ATTRIBUTION_STORAGE_KEY = 'jadecaps_booking_attribution';
const PAID_CLICK_IDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid'];
const PAID_MEDIUMS = new Set([
  'cpc',
  'ppc',
  'paid',
  'paid_social',
  'paidsocial',
  'paid-social',
  'cpm',
  'display',
  'paidsearch',
  'paid_search',
]);

function readAttribution() {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_err) {
    return {};
  }
}

function writeAttribution(next) {
  try {
    sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(next));
  } catch (_err) {
    /* private mode */
  }
}

function attributionEventParams(bookingPath, stored) {
  const params = { booking_path: bookingPath };
  if (stored.utmSource) params.campaign_source = stored.utmSource;
  if (stored.utmMedium) params.campaign_medium = stored.utmMedium;
  if (stored.utmCampaign) params.campaign_name = stored.utmCampaign;
  return params;
}

/**
 * Keep the first tagged landing in this tab (gclid / fbclid / UTM).
 * Later in-app navigations drop the query string, so checkout would otherwise look direct.
 */
export function captureBookingAttribution(search = '') {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(search || '');
  const utmSource = (params.get('utm_source') || '').trim();
  const utmMedium = (params.get('utm_medium') || '').trim();
  const utmCampaign = (params.get('utm_campaign') || '').trim();
  const hasPaidClick = PAID_CLICK_IDS.some((key) => (params.get(key) || '').trim());
  const hasUtm = Boolean(utmSource || utmMedium || utmCampaign);
  if (!hasPaidClick && !hasUtm) return;

  const prev = readAttribution();
  if (prev.captured) return;

  writeAttribution({
    ...prev,
    captured: true,
    isAd: hasPaidClick || PAID_MEDIUMS.has(utmMedium.toLowerCase()),
    utmSource: utmSource || undefined,
    utmMedium: utmMedium || undefined,
    utmCampaign: utmCampaign || undefined,
  });
}

/** Lock this checkout as checkout_link, ad, or website before payment success. */
export function rememberBookingPath({ checkoutLink = false } = {}) {
  const prev = readAttribution();
  const bookingPath = checkoutLink ? 'checkout_link' : prev.isAd ? 'ad' : 'website';
  if (prev.bookingPath !== bookingPath) {
    writeAttribution({ ...prev, bookingPath });
  }
  return attributionEventParams(bookingPath, prev);
}

function purchaseBookingParams() {
  const stored = readAttribution();
  const bookingPath = stored.bookingPath || (stored.isAd ? 'ad' : 'website');
  return attributionEventParams(bookingPath, stored);
}

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function pushDataLayer(payload) {
  if (!isAnalyticsEnabled()) return;
  ensureDataLayer().push(payload);
  log('dataLayer', payload);
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

/** Load GTM container (call early from index.js). */
export function initGoogleTagManager() {
  const { gtmId } = analyticsConfig;
  if (!isAnalyticsEnabled() || !gtmId || window.__jadecapsGtmLoaded) return;

  window.__jadecapsGtmLoaded = true;
  ensureDataLayer().push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);

  log('GTM initialized', gtmId);
}

/** Load gtag.js for direct GA4 / Google Ads (when not fully managed in GTM). */
export function initGtagDirect() {
  if (!isAnalyticsEnabled() || window.__jadecapsGtagLoaded) return;

  const { ga4Id, googleAdsId, ga4ViaGtm, gtmId } = analyticsConfig;
  const shouldLoadGa4 = ga4Id && !(ga4ViaGtm && gtmId);
  const primaryId = shouldLoadGa4 ? ga4Id : googleAdsId;

  if (!primaryId) return;

  window.__jadecapsGtagLoaded = true;
  ensureGtag();

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
  script.onload = () => {
    const gtag = ensureGtag();
    gtag('js', new Date());
    if (shouldLoadGa4) {
      gtag('config', ga4Id, {
        send_page_view: false,
        ...(analyticsConfig.debug ? { debug_mode: true } : {}),
      });
      log('GA4 initialized', ga4Id);
    }
    if (googleAdsId) {
      gtag('config', googleAdsId);
      log('Google Ads initialized', googleAdsId);
    }
  };
  document.head.appendChild(script);
}

/** Load Meta Pixel (Facebook). */
export function initMetaPixel() {
  const { metaPixelId } = analyticsConfig;
  if (!isAnalyticsEnabled() || !metaPixelId || window.fbq) return;

  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  try {
    window.fbq('init', metaPixelId);
    log('Meta Pixel initialized', metaPixelId);
  } catch (_err) {
    /* blocked by ad blocker */
  }
}

/**
 * Load OpenAI Ads Measurement Pixel.
 * Official API: oaiq("init", { pixelId }) then oaiq("measure", eventName, data)
 * https://developers.openai.com/ads/measurement-pixel
 */
export function initOpenAIPixel() {
  const { openAiPixelId, debug } = analyticsConfig;
  if (!isAnalyticsEnabled() || !openAiPixelId || window.oaiq) return;

  /* eslint-disable */
  (function (w, d, s, u) {
    if (w.oaiq) return;
    var q = function () {
      q.q.push(arguments);
    };
    q.q = [];
    w.oaiq = q;
    var js = d.createElement(s);
    js.async = true;
    js.src = u;
    var f = d.getElementsByTagName(s)[0];
    f.parentNode.insertBefore(js, f);
  })(window, document, 'script', 'https://bzrcdn.openai.com/sdk/oaiq.min.js');
  /* eslint-enable */

  try {
    const initPayload = { pixelId: openAiPixelId };
    if (debug) initPayload.debug = true;
    window.oaiq('init', initPayload);
    log('OpenAI Pixel initialized', openAiPixelId);
  } catch (_err) {
    /* blocked by ad blocker */
  }
}

/** ISO 4217 minor units (OpenAI Pixel requires integers). Meta/GA payloads stay in major units. */
function toOpenAiMinorUnits(value) {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

function measureOpenAI(eventName, data) {
  if (typeof window.oaiq !== 'function') return;
  try {
    window.oaiq('measure', eventName, data);
  } catch (_err) {
    /* blocked by ad blocker */
  }
}

function buildOpenAiContentsPayload({ propertyId, propertyName, value, currency = 'INR' } = {}) {
  const amount = toOpenAiMinorUnits(value);
  const payload = { type: 'contents' };
  if (amount != null) {
    payload.amount = amount;
    payload.currency = currency;
  }
  if (propertyId || propertyName) {
    payload.contents = [
      {
        ...(propertyId ? { id: String(propertyId) } : {}),
        ...(propertyName ? { name: propertyName } : {}),
        content_type: 'product',
        quantity: 1,
      },
    ];
  }
  return payload;
}

/** Call once on app boot (after first paint is fine). */
export function initAnalyticsScripts() {
  captureBookingAttribution(typeof window !== 'undefined' ? window.location.search : '');
  if (!isAnalyticsEnabled()) {
    log('Analytics disabled');
    return;
  }

  initGoogleTagManager();
  initGtagDirect();
  initMetaPixel();
  initOpenAIPixel();
}

let lastPageViewKey = '';
let lastPageViewAt = 0;

/** SPA page view — call on every route change. */
export function trackPageView(pathname = window.location.pathname, search = window.location.search) {
  captureBookingAttribution(search);
  if (!isAnalyticsEnabled()) return;

  const pagePath = `${pathname || ''}${search || ''}`;
  const now = Date.now();
  if (pagePath === lastPageViewKey && now - lastPageViewAt < 1000) {
    log('page_view skipped (duplicate)', pagePath);
    return;
  }
  lastPageViewKey = pagePath;
  lastPageViewAt = now;

  pushDataLayer({
    event: 'page_view',
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
  });

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  if (typeof window.fbq === 'function') {
    try {
      window.fbq('track', 'PageView');
    } catch (_err) {
      /* ignore */
    }
  }

  log('page_view', pagePath);
}

export function trackViewContent({ propertyId, propertyName, value, currency = 'INR' } = {}) {
  track('property_view', { propertyId });
  if (!isAnalyticsEnabled()) return;

  const payload = {
    content_ids: propertyId ? [String(propertyId)] : [],
    content_name: propertyName || undefined,
    content_type: 'product',
    value: value != null ? Number(value) : undefined,
    currency,
  };

  pushDataLayer({ event: 'view_item', ...payload });

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'view_item', {
      items: propertyId
        ? [{ item_id: String(propertyId), item_name: propertyName, price: payload.value }]
        : [],
      value: payload.value,
      currency,
    });
  }

  if (typeof window.fbq === 'function') {
    try {
      window.fbq('track', 'ViewContent', payload);
    } catch (_err) {
      /* ignore */
    }
  }

  log('view_item', payload);
}

export function trackInitiateCheckout({
  propertyId,
  propertyName,
  value,
  currency = 'INR',
  checkoutLink = false,
} = {}) {
  const attribution = rememberBookingPath({ checkoutLink });
  if (!isAnalyticsEnabled()) return;

  const payload = {
    content_ids: propertyId ? [String(propertyId)] : [],
    content_name: propertyName || undefined,
    content_type: 'product',
    value: value != null ? Number(value) : undefined,
    currency,
    num_items: 1,
    ...attribution,
  };

  pushDataLayer({ event: 'begin_checkout', ...payload });

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'begin_checkout', {
      value: payload.value,
      currency,
      ...attribution,
      items: propertyId
        ? [{ item_id: String(propertyId), item_name: propertyName, price: payload.value }]
        : [],
    });
  }

  if (typeof window.fbq === 'function') {
    try {
      window.fbq('track', 'InitiateCheckout', payload);
    } catch (_err) {
      /* ignore */
    }
  }

  measureOpenAI('checkout_started', buildOpenAiContentsPayload({ propertyId, propertyName, value, currency }));

  log('begin_checkout', payload);
}

export function trackPurchase({ bookingId, propertyId, propertyName, value, currency = 'INR' } = {}) {
  if (!isAnalyticsEnabled()) return;

  const attribution = purchaseBookingParams();
  const payload = {
    transaction_id: bookingId ? String(bookingId) : undefined,
    content_ids: propertyId ? [String(propertyId)] : [],
    content_name: propertyName || undefined,
    content_type: 'product',
    value: value != null ? Number(value) : undefined,
    currency,
    num_items: 1,
    ...attribution,
  };

  pushDataLayer({ event: 'purchase', ...payload });

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'purchase', {
      transaction_id: payload.transaction_id,
      value: payload.value,
      currency,
      ...attribution,
      items: propertyId
        ? [{ item_id: String(propertyId), item_name: propertyName, price: payload.value }]
        : [],
    });
  }

  if (typeof window.fbq === 'function') {
    try {
      window.fbq('track', 'Purchase', payload);
    } catch (_err) {
      /* ignore */
    }
  }

  measureOpenAI('order_created', buildOpenAiContentsPayload({ propertyId, propertyName, value, currency }));

  log('purchase', payload);
}

export function trackLead({ formName, value } = {}) {
  if (!isAnalyticsEnabled()) return;

  pushDataLayer({ event: 'generate_lead', form_name: formName });

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'generate_lead', { form_name: formName, value });
  }

  if (typeof window.fbq === 'function') {
    try {
      window.fbq('track', 'Lead', { content_name: formName });
    } catch (_err) {
      /* ignore */
    }
  }

  log('generate_lead', formName);
}

export function trackSignUp({ method = 'email' } = {}) {
  if (!isAnalyticsEnabled()) return;

  pushDataLayer({ event: 'sign_up', method });

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'sign_up', { method });
  }

  if (typeof window.fbq === 'function') {
    try {
      window.fbq('track', 'CompleteRegistration');
    } catch (_err) {
      /* ignore */
    }
  }

  log('sign_up', method);
}

export function trackAppDownloadClick({ store, placement } = {}) {
  if (!isAnalyticsEnabled()) return;

  pushDataLayer({ event: 'stayjade_app_click', store, placement });

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'stayjade_app_click', { store, placement });
  }

  log('stayjade_app_click', store, placement);
}
